// The "New agent for <signal>" drawer's rules: which steps it offers, in what order they're allowed, what they cost
// and the agent it creates. Plain functions so the drawer stays a view and the rules stay testable.

export const TELL = "tell";
const MOVES = ["job_change", "champion_move", "left_company"];

/** "company" or "contact": what a saved signal fires on (a custom signal says so on its target). */
export const entityOf = (row, catalog) => {
  const s = (row && row.signal) || {};
  if (s.signalType === "custom") return (((row.custom || {}).target || {}).type) === "contact_list" ? "contact" : "company";
  const def = (catalog || []).find(d => d.id === s.signalType);
  return def && def.entityType === "ACCOUNT" ? "company" : "contact";
};

/** The signal's short name for titles and list names: "Hiring intel", not its scope. */
export const signalNameOf = (row, catalog) => {
  const def = (catalog || []).find(d => d.id === ((row && row.signal) || {}).signalType);
  return (row && row.signalLabel) || (def && def.label) || (row && row.displayName) || "Signal";
};

export const GROUPS = [["all", "All"], ["find", "Find & enrich"], ["do", "Do"], ["tell", "Tell me"], ["flow", "Flow"]];

/**
 * Every step the drawer offers, keyed by the engine's step type. `on`: "company" / "contact" limits it to one kind of
 * signal; `needs`: "people" (company signals: after Find decision-makers) or "email" (after a find-email step);
 * `once`: can only be added one time. Names read as what happens to the record at that point in the chain.
 */
export const STEPS = {
  "expand.people_at_company": { group: "find", icon: "user", tone: "blue", on: "company", once: true,
    name: () => "Find decision-makers", note: () => "Up to 5 per company · free", params: { perCompany: 5 } },
  "expand.find_alternate_contact": { group: "find", icon: "swap", tone: "blue", on: "contact", once: true,
    name: () => "Find who replaced them (same company)", note: () => "Keeps the old account warm", params: { perContact: 1 } },
  "enrich.find_email": { group: "find", icon: "mail", tone: "blue", needs: "people", once: true, price: "1 credit each",
    name: c => (c.move ? "Find their new email" : "Find their emails"), note: () => "1 credit per email", params: { findPhone: "no" } },
  "enrich.research": { group: "find", icon: "book", tone: "amber", once: true, price: "10 credits each",
    name: c => (c.people ? (c.entity === "company" ? "Deep-research each person" : c.move ? "Deep-research them at their new company" : "Deep-research them") : "Deep-research the company"),
    note: () => "10 credits · brief + personal email copy", params: { freshnessDays: 30 } },
  "enrich.account": { group: "find", icon: "building", tone: "blue", once: true,
    name: () => "Fill in the company details", note: () => "Size, industry, links", params: {} },
  "filter.icp_match": { group: "flow", icon: "filter", tone: "blue", needs: "people", once: true,
    name: () => "Keep only people who fit your ICP", note: () => "From a List Builder search", params: {} },
  "act.add_to_account_list": { group: "do", icon: "list", tone: "teal", once: true,
    name: c => (c.entity === "company" && !c.people ? "Add the company to a list" : "Add their company to a list"), note: () => "Free", params: {} },
  "act.add_to_list": { group: "do", icon: "list", tone: "teal", needs: "people", once: true,
    name: c => (c.entity === "contact" ? "Add the person to a list" : "Add to a contact list"), note: () => "Free", params: {} },
  "act.enroll_sequence": { group: "do", icon: "send", tone: "amber", needs: "email", once: true, ask: true,
    name: () => "Add to a sequence", note: () => "Asks you first", params: {} },
  "act.stop_sequence": { group: "do", icon: "stop", tone: "red", on: "contact", once: true,
    name: () => "Stop their current sequence", note: () => "Free", params: { mode: "stop" } },
  "act.update_contact": { group: "do", icon: "edit", tone: "grey", needs: "people", once: true,
    name: () => "Update the contact", note: ctx => (ctx.move ? "Write their new title and company" : "Tag them or set a stage"), params: {} },
  "act.update_account": { group: "do", icon: "stage", tone: "grey", once: true,
    name: () => "Move the account to a stage", note: () => "Pick a stage", params: {} },
  "act.export_to_crm": { group: "do", icon: "cloud", tone: "grey", needs: "people", once: true, ask: true,
    name: () => "Export to CRM", note: () => "Creates or updates them", params: { existing: "update" } },
  "act.create_task": { group: "do", icon: "check", tone: "grey", needs: "people", once: true,
    name: () => "Create a task", note: () => "For the contact owner", params: { dueInDays: 1, assignTo: "contact_owner" } },
  "act.add_note": { group: "do", icon: "edit", tone: "grey", needs: "people", once: true,
    name: () => "Add a note to the contact", note: () => "Free", params: {} },
  [TELL]: { group: "tell", icon: "bell", tone: "grey", once: true, name: () => "Tell me", note: () => "Email or Slack", params: {} },
  "wait.delay": { group: "flow", icon: "clock", tone: "grey", name: () => "Wait a while", note: () => "Hours or days", params: { amount: 3, unit: "days" } },
  // Need the condition editor: offered, but built in the full editor.
  "filter.condition": { group: "flow", icon: "filter", tone: "grey", editor: true, name: () => "Keep only if…", note: () => "A condition", params: {} },
  "branch.if": { group: "flow", icon: "split", tone: "grey", editor: true, name: () => "Split by condition", note: () => "Two paths", params: {} }
};

// What to suggest next, best first; the first three that fit the chain are shown.
const SUGGEST = {
  company: ["expand.people_at_company", "filter.icp_match", "enrich.find_email", "act.enroll_sequence", "enrich.research", "act.update_account", "act.add_to_list", "act.export_to_crm", "act.create_task"],
  contact: ["enrich.find_email", "enrich.research", "expand.find_alternate_contact", "act.enroll_sequence", "act.create_task", "act.export_to_crm", "act.add_note", "wait.delay"]
};

/** Job changes, champion moves and leavers: the person moved, so their old sequence and email are stale. */
export const isMove = row => MOVES.includes(((row && row.signal) || {}).signalType);

/** What the records are and have by position `i` (before the step there): people found yet, emails found yet. */
export const stateAt = (keys, i, entity) => {
  const before = keys.slice(0, i);
  return {
    people: entity === "contact" || before.includes("expand.people_at_company"),
    email: before.includes("enrich.find_email")
  };
};

/** Why `key` can't sit at position `i` of the chain, or "" when it can. */
export const blockedAt = (keys, i, key, entity) => {
  const st = STEPS[key] || {};
  const at = stateAt(keys, i, entity);
  if (st.needs === "people" && !at.people) return "Needs people first";
  if (st.needs === "email" && !at.people) return "Needs people with emails";
  if (st.needs === "email" && !at.email) return entity === "contact" ? "Needs their new email first" : "Needs their emails first";
  return "";
};

/** Every step still sits below what it needs (drag and remove both keep this true). */
export const validOrder = (keys, entity) => keys.every((k, i) => !blockedAt(keys, i, k, entity));

/** Where an added step goes: at the end, but above Tell me, which stays last. */
export const insertAt = keys => (keys[keys.length - 1] === TELL ? keys.length - 1 : keys.length);

/** Steps offered to a signal of this kind (others are never shown). */
export const offeredFor = (entity, catalogTypes) =>
  Object.keys(STEPS).filter(k => (!STEPS[k].on || STEPS[k].on === entity) && (k === TELL || !catalogTypes || catalogTypes.includes(k)));

/** Why a step can't be added now ("" when it can): already there, needs something first, or a missing setup. */
export const addReason = (keys, key, entity, missing = {}) => {
  if (STEPS[key].once && keys.includes(key)) return "Already added";
  if (missing[key]) return missing[key];
  if (STEPS[key].editor) return "Set this up in the full editor";
  return blockedAt(keys, insertAt(keys), key, entity);
};

/** Up to three next steps that fit right now. */
export const suggestionsFor = (keys, entity, catalogTypes, missing) =>
  (SUGGEST[entity] || []).filter(k => offeredFor(entity, catalogTypes).includes(k) && !addReason(keys, k, entity, missing)).slice(0, 3);

/** The note under the suggestions naming what unlocks once people or emails exist ("" when nothing is waiting). */
export const lockNote = (keys, entity) => {
  const at = stateAt(keys, keys.length, entity);
  if (!at.people) return "Find their emails and Add to a sequence appear once it finds people.";
  if (!at.email && !keys.includes("act.enroll_sequence")) return entity === "contact" ? "Add to a sequence appears once it has their new email." : "Add to a sequence appears once it has their emails.";
  return "";
};

/** The steps it starts with: company signals list the company; a person who moved is stopped, tagged and listed. */
export const defaultSteps = (entity, move) =>
  entity === "company" ? ["act.add_to_account_list", TELL]
    : move ? ["act.stop_sequence", "act.update_contact", "act.add_to_list", TELL] : ["act.add_to_list", TELL];

/** The footer's one cost line, from the paid steps in the chain. */
export const costLine = (steps, entity) => {
  const keys = steps.map(s => s.key);
  const parts = [];
  const per = (steps.find(s => s.key === "expand.people_at_company") || { params: {} }).params.perCompany;
  if (keys.includes("enrich.find_email")) parts.push(`1 credit per email found${per ? ` · up to ${per} each time` : ""}`);
  if (keys.includes("expand.find_alternate_contact")) parts.push("1 credit per person found");
  if (keys.includes("enrich.research")) {
    const people = stateAt(keys, keys.indexOf("enrich.research"), entity).people;
    parts.push(`10 credits per ${people ? "person" : "company"} researched`);
  }
  return parts.length ? parts.join(" · ") : "Uses no credits";
};

/** What still has to be picked before it can turn on ("" when ready). */
export const missingPick = steps => {
  const need = { "act.enroll_sequence": ["sequenceId", "Pick a sequence to turn it on."], "act.update_account": ["stage", "Pick a stage to turn it on."],
    "act.export_to_crm": ["crm", "Pick a CRM to turn it on."], "filter.icp_match": ["savedSearchId", "Pick an ICP search to turn it on."],
    "act.add_to_list": ["listId", "Pick a list to turn it on."] };
  const gap = steps.find(s => need[s.key] && !s.params[need[s.key][0]] && !(s.key === "act.add_to_list" && s.params.newListName));
  return gap ? need[gap.key][1] : "";
};

const node = (n, stepType, params) => ({ nodeId: `s${n}_${stepType.replace(/\W/g, "_")}`, stepType, params: params || {}, errorPolicy: "skip" });

/**
 * The agent Turn on creates: the signal trigger, the source, the skip rules, then the steps in order. Tell me becomes
 * an email and/or Slack node (a daily digest is the agent's notify mode instead). Do-not-contact and opted-out people
 * are dropped right before the first step that reaches outside Salesgear.
 */
export const buildAgent = ({ row, name, steps, skipSeen, skipInSequence, timezone }) => {
  const s = (row && row.signal) || {};
  const typed = String(s.id || "").startsWith("type:") ? s.id.slice(5) : "";
  const flow = [node(1, "source.from_event")];
  if (skipSeen) flow.push(node(2, "filter.suppress_seen"));
  let notifyMode = "ON_FAILURE";
  let guarded = false;
  steps.forEach(st => {
    if (STEPS[st.key].ask && !guarded) {
      flow.push(node(flow.length + 1, "filter.deliverable"));
      guarded = true;
    }
    if (st.key === TELL) {
      const { mode, channel } = st.params;
      if (mode === "digest") notifyMode = "DAILY_DIGEST";
      if (mode === "email" || mode === "both") flow.push(node(flow.length + 1, "notify.email"));
      if (mode === "slack" || mode === "both") flow.push(node(flow.length + 1, "notify.slack", channel ? { channel } : {}));
      return;
    }
    const params = { ...st.params };
    delete params.newListName;
    if (st.key === "act.enroll_sequence" && skipInSequence) params.skipActive = true;
    flow.push(node(flow.length + 1, st.key, params));
  });
  return {
    name,
    description: "Runs when your signal fires.",
    templateKey: "signal_agent",
    status: "ACTIVE",
    trigger: { type: "signal.saved", config: typed ? { signalType: typed } : { savedSignalId: s.id } },
    config: {
      approvalMode: steps.some(st => STEPS[st.key].ask) ? "REQUIRED" : "NONE",
      notifyMode,
      timezone,
      ...(s.sellerProfileId ? { sellerProfileId: s.sellerProfileId } : {})
    },
    flow
  };
};
